/**
 * Phase J-UX — Redesigned journal entry card.
 *
 * Card anatomy (top → bottom):
 *   [Visibility chip · Mood chip · tag chips · meta dot + dot]
 *   [Title (large, heading font)]
 *   [Short preview — 2 to 3 lines, "Read more" toggle when longer]
 *   [Linked-records row: child chips, calendar source chip, future
 *    expense / court-order / contact-visit chips — anything in
 *    `entry.links[]`]
 *   [Footer: location · attachments · timestamp · author]
 *   [Right rail: Open button + ••• overflow menu]
 *
 * All previously-loud actions (PDF, Share, Make private, Edit,
 * Delete) live inside the ••• menu so the card content shines.
 *
 * Read-more state is local to each card.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Lock,
  Eye,
  MapPin,
  Paperclip,
  CalendarDays,
  MoreHorizontal,
  Download,
  Pencil,
  Trash2,
  Share2,
  ShieldCheck,
  ArrowUpRight,
  FileText,
  ChevronDown,
  ChevronUp,
  Link2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MOOD_META } from "@/components/journal/MoodPicker";

const MOOD_BY_VALUE = Object.fromEntries(MOOD_META.map((m) => [m.value, m]));

const LINK_TYPE_META = {
  expense: { Icon: FileText, label: "Expense" },
  court_order: { Icon: ShieldCheck, label: "Court order" },
  contact_visit: { Icon: CalendarDays, label: "Contact visit" },
  event: { Icon: CalendarDays, label: "Calendar event" },
  document: { Icon: FileText, label: "Document" },
  request: { Icon: ArrowUpRight, label: "Request" },
  message: { Icon: FileText, label: "Message" },
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function previewText(body, maxChars = 240) {
  if (!body) return { preview: "", truncated: false };
  // 2–3 lines roughly equals ~240 chars; respect natural breaks.
  const trimmed = body.trim();
  if (trimmed.length <= maxChars) {
    return { preview: trimmed, truncated: false };
  }
  let cut = trimmed.slice(0, maxChars);
  const lastSpace = cut.lastIndexOf(" ");
  if (lastSpace > maxChars * 0.6) cut = cut.slice(0, lastSpace);
  return { preview: cut + "…", truncated: true };
}

function ChildChip({ child }) {
  return (
    <span
      className="text-[11px] px-2 py-0.5 rounded-full border bg-white border-[#D9E1E3] text-[#3F535A] inline-flex items-center gap-1"
      data-testid={`journal-card-child-${child.id}`}
    >
      <span
        className="w-2 h-2 rounded-full"
        style={{ background: child.color || "#1F9FA4" }}
      />
      {child.name}
    </span>
  );
}

export function JournalEntryCard({
  entry,
  childrenById,
  currentUserId,
  onOpen,
  onEdit,
  onShareToggle,
  onDelete,
  onExportPdf,
}) {
  const [expanded, setExpanded] = useState(false);

  const mood = MOOD_BY_VALUE[entry.mood];
  const MoodIcon = mood?.Icon;
  const isAuthor = entry.author_id === currentUserId;

  const childChips = (entry.child_ids || [])
    .map((cid) => childrenById?.[cid])
    .filter(Boolean);

  const { preview, truncated } = previewText(entry.body || "");
  const visibleText = expanded ? entry.body : preview;

  // Build a single normalised array of linked records:
  // legacy source_event_id gets promoted to a synthetic link.
  const allLinks = [
    ...(entry.source_event_id
      ? [
          {
            type: "event",
            id: entry.source_event_id,
            label: entry.source_event_title || "Calendar event",
            href: `/app/calendar?spotlight=event-${entry.source_event_id}`,
          },
        ]
      : []),
    ...((entry.links || []).map((l) => ({
      ...l,
      href: null, // explicit links don't auto-link until module wiring lands
    }))),
  ];

  return (
    <article
      className="border border-[#D9E1E3] bg-white rounded-md p-4 sm:p-5 hover:shadow-sm transition-shadow"
      data-testid={`journal-card-${entry.id}`}
    >
      {/* Top meta row: visibility, mood, mood tags */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${
              entry.is_shared
                ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]"
                : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"
            }`}
            data-testid={`journal-card-visibility-${entry.id}`}
          >
            {entry.is_shared ? (
              <Eye className="w-3 h-3" strokeWidth={1.7} />
            ) : (
              <Lock className="w-3 h-3" strokeWidth={1.7} />
            )}
            {entry.is_shared ? "Shared" : "Private"}
          </span>
          {mood ? (
            <span
              className="text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1"
              style={{ color: mood.color, background: mood.bg, borderColor: mood.color }}
              data-testid={`journal-card-mood-${entry.id}`}
            >
              {MoodIcon ? <MoodIcon className="w-3 h-3" strokeWidth={1.7} /> : null}
              {mood.label}
            </span>
          ) : null}
          {(entry.mood_tags || []).slice(0, 4).map((t) => (
            <span
              key={t}
              className="text-[10px] tracking-[0.05em] px-2 py-0.5 border rounded-full bg-white border-[#D9E1E3] text-[#4A6170] capitalize"
              data-testid={`journal-card-tag-${entry.id}-${t}`}
            >
              {t}
            </span>
          ))}
        </div>

        {/* Right rail: Open + ••• menu */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onOpen && onOpen(entry)}
            className="text-[#1F9FA4] hover:text-[#178085] hover:bg-[#E0F2F2] h-8 px-2"
            data-testid={`journal-card-open-${entry.id}`}
          >
            Open
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="h-8 w-8 inline-flex items-center justify-center rounded-sm hover:bg-[#F4FAFA] text-[#6F828A]"
                aria-label="More actions"
                data-testid={`journal-card-more-${entry.id}`}
              >
                <MoreHorizontal className="w-4 h-4" strokeWidth={1.7} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-[#D9E1E3]"
              data-testid={`journal-card-menu-${entry.id}`}
            >
              <DropdownMenuItem
                onClick={() => onOpen && onOpen(entry)}
                data-testid={`journal-card-menu-open-${entry.id}`}
              >
                <Eye className="w-3.5 h-3.5 mr-2" /> Open
              </DropdownMenuItem>
              {isAuthor ? (
                <>
                  <DropdownMenuItem
                    onClick={() => onEdit && onEdit(entry)}
                    data-testid={`journal-card-menu-edit-${entry.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onShareToggle && onShareToggle(entry)}
                    data-testid={`journal-card-menu-share-${entry.id}`}
                  >
                    <Share2 className="w-3.5 h-3.5 mr-2" />
                    {entry.is_shared ? "Make private" : "Share with co-parent"}
                  </DropdownMenuItem>
                </>
              ) : null}
              <DropdownMenuItem
                onClick={() => onExportPdf && onExportPdf(entry)}
                data-testid={`journal-card-menu-pdf-${entry.id}`}
              >
                <Download className="w-3.5 h-3.5 mr-2" /> Download PDF
              </DropdownMenuItem>
              {isAuthor ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete && onDelete(entry)}
                    className="text-[#7E3B25] focus:text-[#7E3B25]"
                    data-testid={`journal-card-menu-delete-${entry.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Title — the focus of the card */}
      <h3
        className="font-heading text-[17px] sm:text-lg font-medium mt-2 text-[#0B2E32] leading-snug"
        data-testid={`journal-card-title-${entry.id}`}
      >
        {entry.title}
      </h3>

      {/* Body preview */}
      {visibleText ? (
        <p
          className="text-sm text-[#3F535A] mt-2 whitespace-pre-wrap leading-relaxed"
          data-testid={`journal-card-body-${entry.id}`}
        >
          {visibleText}
        </p>
      ) : null}
      {truncated ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-xs text-[#1F9FA4] hover:text-[#178085] inline-flex items-center gap-1"
          data-testid={`journal-card-readmore-${entry.id}`}
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3 h-3" strokeWidth={1.7} /> Read less
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" strokeWidth={1.7} /> Read more
            </>
          )}
        </button>
      ) : null}

      {/* Linked-records row */}
      {(childChips.length > 0 || allLinks.length > 0) ? (
        <div
          className="flex items-center gap-1.5 flex-wrap mt-3"
          data-testid={`journal-card-links-${entry.id}`}
        >
          {childChips.map((c) => (
            <ChildChip key={c.id} child={c} />
          ))}
          {allLinks.map((lnk, idx) => {
            const meta = LINK_TYPE_META[lnk.type] || LINK_TYPE_META.event;
            const Inner = (
              <span
                className="text-[11px] px-2 py-0.5 rounded-full border bg-[#E0F2F2] border-[#A7D8DA] text-[#116B6E] inline-flex items-center gap-1"
                data-testid={`journal-card-link-${entry.id}-${idx}`}
              >
                <meta.Icon className="w-3 h-3" strokeWidth={1.7} />
                {lnk.label || meta.label}
              </span>
            );
            return lnk.href ? (
              <Link key={`${lnk.type}-${lnk.id}-${idx}`} to={lnk.href}>
                {Inner}
              </Link>
            ) : (
              <React.Fragment key={`${lnk.type}-${lnk.id}-${idx}`}>{Inner}</React.Fragment>
            );
          })}
        </div>
      ) : null}

      {/* Footer: location · attachments · time · author */}
      <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px] text-[#6F828A]">
        {entry.location ? (
          <span className="inline-flex items-center gap-1" data-testid={`journal-card-location-${entry.id}`}>
            <MapPin className="w-3 h-3" strokeWidth={1.6} />
            {entry.location}
          </span>
        ) : null}
        {(entry.attachment_ids || []).length > 0 ? (
          <span className="inline-flex items-center gap-1">
            <Paperclip className="w-3 h-3" strokeWidth={1.6} />
            {entry.attachment_ids.length} attachment
            {entry.attachment_ids.length === 1 ? "" : "s"}
          </span>
        ) : null}
        {(entry.attachment_ids || []).length > 0 || entry.location ? <span>·</span> : null}
        <span data-testid={`journal-card-time-${entry.id}`}>{fmtTime(entry.created_at)}</span>
        <span>·</span>
        <span data-testid={`journal-card-author-${entry.id}`}>{entry.author_name}</span>
        {!childChips.length && allLinks.length === 0 ? (
          <span
            className="inline-flex items-center gap-1 ml-auto text-[#6F828A]"
            data-testid={`journal-card-no-links-${entry.id}`}
          >
            <Link2 className="w-3 h-3" strokeWidth={1.6} />
            No linked records
          </span>
        ) : null}
      </div>

      {/* Tiny date stamp for visual reinforcement at the very bottom */}
      <span className="sr-only">{fmtDate(entry.created_at)}</span>
    </article>
  );
}

export default JournalEntryCard;
